import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { nanoid } from "nanoid";
import useDebounce from "hooks/useDebounce";
import useUsers from "hooks/useUsers";
import { IFilterPagingParams, IUsers } from "utils/api.interfaces";

interface AddOrganizationAdminProps {
  onAdminSelected: (user: IUsers) => void;
}

const AddOrganizationAdmin: React.FC<AddOrganizationAdminProps> = ({
  onAdminSelected,
}) => {
  const [selectedUser, setSelectedUser] = useState<IUsers | null>(null);
  const [filterValue, setFilterValue] = useState<string>("");
  const [filterInputLoader, setFilterInputLoader] =
    React.useState<boolean>(false);
  const debouncedValue = useDebounce<string>(filterValue, 500);

  const initialSearchParams: IFilterPagingParams = {
    page: "0",
    filter: "",
    pageSize: "50",
  };

  const { isLoading, data } = useUsers(
    {
      filter: filterValue,
      page: initialSearchParams.page,
      pageSize: initialSearchParams.pageSize,
    },
    {
      refetchOnReconnect: "always",
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  React.useEffect(() => {
    setFilterInputLoader(false);
  }, [debouncedValue]);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value);
    setFilterInputLoader(true);
  };

  const handleUserSelect = (user: IUsers) => {
    setSelectedUser(user);
  };

  const handleAddAdmin = () => {
    if (selectedUser) {
      onAdminSelected(selectedUser);
      // Reset form
      setSelectedUser(null);
      setFilterValue("");
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Add Organization Admin
        </Typography>
        <Stack direction="row" spacing={4}>
          {/* Left side - User Search */}
          <Stack spacing={2} sx={{ width: "50%" }}>
            <TextField
              margin="dense"
              id="filter"
              label="Search users"
              InputLabelProps={{
                shrink: true,
              }}
              variant="standard"
              onChange={handleChangeFilter}
              value={filterValue}
              sx={{ width: "100%" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="" edge="end">
                      {filterInputLoader ? (
                        <CircularProgress size={15} />
                      ) : (
                        <FilterListIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {filterValue && (
              <List
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                {data?.users.map((user) => (
                  <ListItem
                    key={nanoid(6)}
                    button
                    onClick={() => handleUserSelect(user)}
                    selected={selectedUser?.id === user.id}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                      },
                      paddingTop: "1px",
                      paddingBottom: "1px",
                    }}
                  >
                    <ListItemIcon>
                      <Avatar
                        alt={`${user.firstName} ${user.lastName}`}
                        src={
                          user.imageUrl ? user.imageUrl : user.profileImageUrl
                        }
                        sx={{ width: 40, height: 40, marginRight: "10px" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                      secondary={user.email}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>

          {/* Right side - Selected User */}
          <Stack spacing={2} sx={{ width: "50%" }}>
            {selectedUser ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                    src={
                      selectedUser.imageUrl
                        ? selectedUser.imageUrl
                        : selectedUser.profileImageUrl
                    }
                    sx={{ width: 64, height: 64 }}
                  />
                  <Stack>
                    <Typography variant="h6">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Stack>
                </Stack>

                <Typography variant="body1" color="text.secondary">
                  This user will be added as an admin to the organization. Organization admins have full management permissions.
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddAdmin}
                  sx={{ mt: 2 }}
                >
                  Add Admin
                </Button>
              </>
            ) : (
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                <Typography variant="body1" color="text.secondary">
                  {filterValue
                    ? "Select a user from the search results"
                    : "Search for a user to add as organization admin"}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AddOrganizationAdmin;